/**
 * JSON snapshot export: PackMetadata at the top level, then the design, provenance,
 * profiles, derived parts (mm), openings, datums, sheet fit, validation and revisions.
 * Allowed for invalid designs (debugging) and then carries `validation.ok: false`.
 */
import type { AutosaveSnapshot, Design, Fields, Revision } from '@/domain/schemas';
import { SCHEMA_VERSION } from '@/domain/schemas';
import { CONSTRUCTION, type ConstructionProfile } from '@/domain/profiles';
import { familyOf } from '@/families/registry';
import type { AnyStock } from '@/families/stock';
import type { Store } from '@/domain/store';
import { toMm } from '@/domain/units';
import { exportBlocker, packMetadata } from './common';

export interface JsonSource {
  design: Design;
  fields?: Fields;
  revisions?: Revision[];
  currentRevisionId?: string | null;
}

export interface JsonOptions {
  revisionId?: string | null;
  generatedAt?: string;
  stock?: AnyStock;
  construction?: ConstructionProfile;
}

function isStore(s: unknown): s is Store {
  return !!s && typeof (s as Store).snapshot === 'function';
}

/** Pretty JSON of a store, an AutosaveSnapshot or a {design, fields?, revisions?, currentRevisionId?} object. */
export function toJSON(source: Store | AutosaveSnapshot | JsonSource, opts: JsonOptions = {}): string {
  let src: JsonSource;
  let stock: AnyStock | undefined = opts.stock;
  let construction = opts.construction ?? CONSTRUCTION;
  if (isStore(source)) {
    src = { design: source.design, fields: source.fields, revisions: source.revisions, currentRevisionId: source.currentRevisionId };
    stock = opts.stock ?? source.stock;
    construction = opts.construction ?? source.construction;
  } else if ('state' in source) {
    src = source.state;
  } else {
    src = source;
  }
  const build = familyOf(src.design).generate(src.design, stock, construction);
  const meta = packMetadata(build, { revisionId: opts.revisionId !== undefined ? opts.revisionId : (src.currentRevisionId ?? null), ...(opts.generatedAt ? { generatedAt: opts.generatedAt } : {}) });
  const out = {
    schemaVersion: SCHEMA_VERSION,
    kind: 'cutlist-maker/pack',
    ...meta,
    blocker: exportBlocker(build, src.fields ?? {}),
    design: { ...src.design, units: '0.1 mm ticks' },
    fields: src.fields ?? null,
    familyId: build.familyId,
    familyVersion: build.familyVersion,
    familyLabel: build.familyLabel,
    stock: build.stock,
    stocks: build.stocks,
    construction,
    parts: build.parts.map((p) => ({
      id: p.id, role: p.role, label: p.label, qty: p.qty, material: p.material, stockId: p.stockId,
      ...(p.glueUp ? { glueUp: { strips: p.glueUp.strips, stripWidthMm: toMm(p.glueUp.stripWidthTicks) } } : {}),
      finishedMm: { l: toMm(p.finished.l), w: toMm(p.finished.w), t: toMm(p.finished.t) },
      cutMm: { l: toMm(p.cut.l), w: toMm(p.cut.w), t: toMm(p.cut.t) },
      grainAxis: p.grainAxis, grainWorld: p.grainWorld, edge: p.edge, face: p.face, placementMm: p.placement,
      ...(p.datum !== undefined ? { datumMm: p.datum } : {}),
    })),
    joints: build.joints,
    hardware: build.hardware,
    openings: build.openings.map((o) => ({ id: o.id, index: o.index, ...(o.bay !== undefined ? { bay: o.bay } : {}), label: o.label, clearMm: o.clearMm, bottomDatum: o.bottomDatum, topDatum: o.topDatum, bounds: o.bounds })),
    datums: build.datums,
    shelfDatumsMm: build.shelfDatums,
    sheetFit: build.sheetFit,
    validation: build.validation,
    versions: build.versions,
    revisions: (src.revisions ?? []).map((r) => ({ id: r.id, label: r.label, kind: r.kind, manufacturingHash: r.manufacturingHash, appearanceHash: r.appearanceHash, createdAt: r.createdAt, parentRevisionId: r.parentRevisionId ?? null })),
  };
  return JSON.stringify(out, null, 2);
}
